import Vue from 'vue';
import Vuelidate from 'vuelidate';
import VueCurrencyFilter from 'vue-currency-filter';
import VueGtm from '@gtm-support/vue2-gtm';

import { Auth0, Eclipx, EUINotifications, Auth0Guard, Router } from 'eclipx.ui';
import appConfig from './utils/app-config';
import loadGoogleMaps from './utils/load-google-maps';

import routes from './router/Routes';

import { createProvider } from './vue-apollo';

import store from './store';

import ScoreboardApp from './ScoreboardApp';

import './styles/index.sass';

// Install and use LoadScript plugn
import LoadScript from 'vue-plugin-load-script';

import EUISessionKicker from '@eui-plugin/session-kicker/src';
import { Notify, Dialog } from 'quasar';
import { oneTagTracker } from './utils/one-tag-tracker';

Vue.use(LoadScript);

Vue.config.productionTip = false;

// Install the base Eclipx UI plugin
Vue.use(Eclipx, {
  quasar: {
    plugins: {
      Notify,
      Dialog
    }
  }
});
Vue.use(EUINotifications);

Vue.use(Auth0, {
  domain: appConfig.VUE_APP_AUTH_DOMAIN,
  clientId: appConfig.VUE_APP_AUTH_CLIENT_ID,
  connection: appConfig.VUE_APP_AUTH_CONNECTION,
  onRedirectCallback: (appState) => {
    router.push(appState && appState.targetUrl ? appState.targetUrl : window.location.pathname);
  }
});

async function mount() {
  await loadGoogleMaps({
    key: process.env.VUE_APP_GOOGLE_KEY,
    libraries: ['places']
  });
}

// Setup Routing with Eclipx UI Vue Router plugin and application routes
const router = Router(Vue, { routes });

// Setup GTM
Vue.use(VueGtm, {
  id: appConfig.VUE_APP_GTM_ID,
  queryParams: {
    gtm_auth: appConfig.VUE_APP_GTM_AUTH,
    gtm_preview: appConfig.VUE_APP_GTM_PREVIEW
  }
});

// Add Authentication redirection
router.beforeEach(Auth0Guard);

// Adding one tag tracker
router.afterEach((to, from) => {
  if (to.path.startsWith('/calculator') && to.path !== '/calculator/thank-you') {
    oneTagTracker.trackCalculatorPage(to);
  }
});
// Add Vuelidate for component form validation
Vue.use(Vuelidate);

// Filters
Vue.use(VueCurrencyFilter, {
  symbol: '$',
  thousandsSeparator: ',',
  fractionCount: 2,
  fractionSeparator: '.',
  symbolPosition: 'front',
  symbolSpacing: true
});

// 15 min kickout
const installSessionKicker = () => {
  const sessionKickerConfig = {
    extendSessionCallBack: async () => {
      const sessionKicker = Vue.prototype.$euiSessionKicker;

      // Only resetting the timer if user is authenticated
      if (Vue.prototype.$auth && Vue.prototype.$auth.isAuthenticated) {
        if (sessionKicker && sessionKicker.resetTimer) {
          sessionKicker.resetTimer();
        }
      }
    },
    preLogoutHandler: () => {
      const auth = Vue.prototype.$auth;

      // Only logging out if authenticated
      if (auth && auth.isAuthenticated) {
        auth.auth0Client.logout({ localOnly: true });
        auth.$set(auth, 'isAuthenticated', false);
      }
    },
    logoutCallback: () => {
      router.push({ name: 'logout' });
    }
  };

  Vue.use(EUISessionKicker, sessionKickerConfig);
};

// Managing session kicker based on authentication state
router.beforeEach((to, from, next) => {
  const isAuthenticated = Vue.prototype.$auth && Vue.prototype.$auth.isAuthenticated;

  if (isAuthenticated) {
    if (!Vue.prototype.$euiSessionKicker) {
      installSessionKicker();
    }
  } else {
    // Disable session kicker if user is not authenticated
    disableSessionKicker();
  }

  next();
});

function disableSessionKicker() {
  if (Vue.prototype.$euiSessionKicker) {
    try {
      // Clear session timeout
      if (Vue.prototype.$euiSessionKicker._sessionTimeout) {
        clearTimeout(Vue.prototype.$euiSessionKicker._sessionTimeout);
        Vue.prototype.$euiSessionKicker._sessionTimeout = null;
      }

      // Clear warning timeout
      if (Vue.prototype.$euiSessionKicker._warningTimeout) {
        clearTimeout(Vue.prototype.$euiSessionKicker._warningTimeout);
        Vue.prototype.$euiSessionKicker._warningTimeout = null;
      }
    } catch (e) {
      // eslint-disable-next-line
      console.error('Failed to clear session timeouts', e);
    }
  }
}

new Vue({
  router,
  store,
  apolloProvider: createProvider(),
  render: (h) => h(ScoreboardApp)
}).$mount('#app');

mount().catch((e) => {
  // eslint-disable-next-line
  console.error(e);
});
